/** Erreur d'API portant le code stable renvoyé par le serveur. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly payload: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

/** Le jeton anti-CSRF est déposé dans un cookie lisible : c'est le principe du double envoi. */
function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)uhc_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

/** Signale une session expirée pour que le fournisseur de session se remette à zéro. */
const SESSION_LOST = 'uhc:session-lost';
export function onSessionLost(handler: () => void): () => void {
  window.addEventListener(SESSION_LOST, handler);
  return () => window.removeEventListener(SESSION_LOST, handler);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(method === 'GET' ? {} : { 'X-CSRF-Token': csrfToken() }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    // Panne réseau : distinguée d'une erreur applicative, le message à afficher n'est
    // pas le même et l'utilisateur ne doit pas croire que sa saisie est en cause.
    throw new ApiError(0, 'network');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: Record<string, unknown> = {};
  try {
    payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    payload = {};
  }

  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event(SESSION_LOST));
    const code = typeof payload.error === 'string' ? payload.error : `http_${response.status}`;
    throw new ApiError(response.status, code, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
